<script>
import { questionTypeLabel } from '@/constants/question_types';
import { QUESTION_SECTIONS } from '@/constants/question_sections';
import FilterPanel from '@/components/common/FilterPanel.vue';
import FilterField from '@/components/common/FilterField.vue';
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue';
import SavedFilters from '@/components/common/SavedFilters.vue';
import { SAVED_FILTER_SCOPES } from '@/shared/services/saved-filter.service';

/**
 * Les critères de la liste des questions.
 *
 * Tout le catalogue est chargé d'un coup: le tri se fait donc dans la page, et
 * les critères peuvent porter sur ce que la base ne saurait pas filtrer seule —
 * le fait qu'une question propose des options, par exemple. Sections et types
 * se cochent à plusieurs: une refonte de questionnaire regarde rarement une
 * seule section à la fois.
 */
export default {
  name: 'QuestionFilters',
  components: {
    FilterPanel,
    FilterField,
    MultiSelectFilter,
    SavedFilters,
  },
  props: {
    modelValue: { type: Object, required: true },
    /** Uppercase type keys, e.g. SHORT_TEXT. */
    typesList: { type: Array, default: () => [] },
    typesLookup: { type: Object, default: () => ({}) },
    activeCount: { type: Number, default: 0 },
    criteria: { type: Object, default: () => ({}) },
    appliedFilterId: { type: String, default: null },
    expanded: { type: Boolean, default: false },
  },
  emits: [
    'update:modelValue',
    'update:expanded',
    'reset',
    'apply-saved',
    'saved-filters-loaded',
  ],
  data() {
    return {
      scope: SAVED_FILTER_SCOPES.QUESTIONS,
      statusOptions: [
        { value: '', label: 'Visibles et masquées' },
        { value: 'visible', label: 'Visibles' },
        { value: 'hidden', label: 'Masquées' },
      ],
      requiredOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Obligatoires' },
        { value: 'no', label: 'Facultatives' },
      ],
      optionOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Avec options' },
        { value: 'no', label: 'Sans options' },
      ],
    };
  },
  computed: {
    /** Les six sections du questionnaire, plus les questions non assignées. */
    sectionChoices() {
      return [
        ...QUESTION_SECTIONS.map((section, index) => ({
          value: index,
          label: section.name,
        })),
        { value: -1, label: 'Non assignée' },
      ];
    },

    typeChoices() {
      return this.typesList.map((key) => ({
        value: key,
        label: questionTypeLabel(key, this.typesLookup),
      }));
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
  },
};
</script>

<template>
  <FilterPanel
    :model-value="modelValue.search"
    :expanded="expanded"
    :active-count="activeCount"
    search-placeholder="Rechercher un libellé, une aide…"
    @update:model-value="update('search', $event)"
    @update:expanded="$emit('update:expanded', $event)"
    @reset="$emit('reset')"
  >
    <template #quick>
      <select
        class="list-select"
        :value="modelValue.status"
        aria-label="Filtrer par statut"
        data-testid="questions-status"
        @change="update('status', $event.target.value)"
      >
        <option
          v-for="option in statusOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </template>

    <template #saved>
      <SavedFilters
        :scope="scope"
        :criteria="criteria"
        :applied-id="appliedFilterId"
        @apply="$emit('apply-saved', $event)"
        @loaded="$emit('saved-filters-loaded', $event)"
      />
    </template>

    <template #advanced>
      <FilterField label="Sections">
        <MultiSelectFilter
          :model-value="modelValue.sections"
          :options="sectionChoices"
          placeholder="Toutes les sections"
          @update:model-value="update('sections', $event)"
        />
      </FilterField>

      <FilterField label="Types de réponse">
        <MultiSelectFilter
          :model-value="modelValue.types"
          :options="typeChoices"
          placeholder="Tous les types"
          @update:model-value="update('types', $event)"
        />
      </FilterField>

      <FilterField label="Obligation">
        <select
          class="list-select"
          :value="modelValue.required"
          data-testid="questions-required"
          @change="update('required', $event.target.value)"
        >
          <option
            v-for="option in requiredOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Options de réponse">
        <select
          class="list-select"
          :value="modelValue.hasOptions"
          data-testid="questions-has-options"
          @change="update('hasOptions', $event.target.value)"
        >
          <option
            v-for="option in optionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>
    </template>
  </FilterPanel>
</template>
